/**
 * Dashboard panel mapping connected GitHub repositories to the projects
 * that use them. Complements the zero-state banner: once a user HAS
 * installations, this is the persistent surface showing what's connected,
 * what it's used for, and how to connect more. "Use" hands the repo URL to
 * the New Project form via onUseRepo.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Project } from '@veodiagram/shared';
import { api } from '../../services/api';
import { rememberGithubReturnPath } from './returnPath';

interface Repo {
  fullName: string;
  private: boolean;
  htmlUrl: string;
  installationId: string;
}

function normalizeRepoUrl(url: string | null | undefined): string {
  if (!url) return '';
  return url.toLowerCase().replace(/\.git$/, '').replace(/\/+$/, '');
}

export function ConnectedReposPanel({
  projects,
  onUseRepo,
}: {
  projects: Project[];
  onUseRepo: (htmlUrl: string) => void;
}) {
  const [repos, setRepos] = useState<Repo[] | null>(null);
  const [installUrl, setInstallUrl] = useState<string>('');
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = await api.getGithubAppInfo();
        if (!info.configured || !info.installUrl) return;
        const { installations } = await api.listGithubInstallations();
        if (installations.length === 0) return; // zero-state → banner's job
        const { repos: r } = await api.listGithubRepos();
        if (!cancelled) {
          setInstallUrl(info.installUrl);
          setRepos(r);
        }
      } catch { /* signed out or unavailable — render nothing */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!repos || repos.length === 0) return null;

  const projectsByRepo = new Map<string, Project[]>();
  for (const p of projects) {
    const key = normalizeRepoUrl(p.repoUrl);
    if (!key) continue;
    projectsByRepo.set(key, [...(projectsByRepo.get(key) ?? []), p]);
  }

  return (
    <section className="mb-8 rounded-lg border border-border">
      <div className="flex items-center justify-between px-4 py-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm font-semibold"
          aria-expanded={expanded}
        >
          <span className={`inline-block transition-transform ${expanded ? 'rotate-90' : ''}`}>▸</span>
          Connected GitHub repositories
          <span className="text-muted-foreground font-normal">({repos.length}, read-only)</span>
        </button>
        <a
          href={installUrl}
          target="_blank"
          rel="noreferrer"
          onClick={() => rememberGithubReturnPath('/dashboard')}
          className="text-sm text-primary hover:underline whitespace-nowrap"
        >
          Connect more →
        </a>
      </div>
      {expanded && (
        <ul className="border-t border-border divide-y divide-border/60">
          {repos.map((r) => {
            const used = projectsByRepo.get(normalizeRepoUrl(r.htmlUrl)) ?? [];
            return (
              <li key={r.fullName} className="flex flex-wrap items-center gap-2 px-4 py-2.5">
                <a
                  href={r.htmlUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium hover:underline"
                >
                  {r.fullName}
                </a>
                {r.private && (
                  <span className="rounded border border-border px-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                    private
                  </span>
                )}
                <span className="mx-1 text-muted-foreground/50" aria-hidden>·</span>
                {used.length > 0 ? (
                  <span className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                    used in
                    {used.map((p) => (
                      <Link
                        key={p.id}
                        to={`/projects/${p.id}`}
                        className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground hover:bg-muted/70"
                      >
                        {p.name}
                      </Link>
                    ))}
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    not used in any project yet
                    <button
                      onClick={() => onUseRepo(r.htmlUrl)}
                      className="rounded border border-primary/40 px-1.5 py-0.5 text-xs text-primary hover:bg-primary/10"
                    >
                      + Create project
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
